import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "@emotion/styled";
import { inputBorders, inputMenu } from "../../../../../styles/styledSnippets";
import { buckets } from "../../../../../utils/constants/dataTypes";
import ExplorerSelectPortal from "../../NavigationTable/ExplorerSelectPortal";
import useOnClickOutside from "../../../../../hooks/useOnClickOutside";
import Option from "./Option";

const Container = styled.div`
  position: relative;
`;

const Menu = styled.div`
  ${(props) => inputMenu(props)}
  & > div:last-of-type {
    border: none;
  }
  display: inline-block;
  position: absolute;
  top: ${(props) => props.dimensions.top + props.dimensions.height}px;
  left: ${(props) => props.dimensions.left}px;
  white-space: nowrap;
`;

export const Value = styled.div`
  width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  ${(props) => inputBorders(props)}

  path {
    fill: ${(props) => props.theme.text.secondary};
  }
  ${(props) =>
    props.isDisabled &&
    `
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const operators = {
  string: [
    { value: "==", label: "Equal", icon: "equals" },
    { value: "!=", label: "Not Equal", icon: "not-equal" },
    { value: "like", label: "Partial Match", icon: "search" },
  ],
  number: [
    { value: "==", label: "Equal", icon: "equals" },
    { value: ">", label: "Greater", icon: "greater-than" },
    { value: ">=", label: "Greater or Equal", icon: "greater-than-equal" },
    { value: "<", label: "Less", icon: "less-than" },
    { value: "<=", label: "Less or Equal", icon: "less-than-equal" },
  ],
  date: [
    { value: ">=", label: "Since", icon: "angle-double-right" },
    { value: "<=", label: "Before", icon: "angle-double-left" },
    { value: "==", label: "Equal", icon: "equals" },
    { value: "range", label: "Range", icon: "brackets" },
    {
      value: "lastDays",
      label: "Rolling Day Filter",
      icon: "calendar-day",
      description: `Definition: The "Rolling Day Filter" allows users to view data starting from today and extending back a specified number of days.\n
        Usage:\n
        A value of 1 includes only today.\n
        A value of 2 includes today and yesterday.\n
        A value of n includes today and the previous n-1 days.\n`,
    },
    {
      value: "lastDaysNoToday",
      label: "Rolling Days Excluding Today",
      icon: "calendar-day",
      description: `Definition: The "Rolling Days Excluding Today" allows users to view data starting from yesterday and extending back a specified number of days.\n
        Usage:\n
        A value of 1 includes only yesterday.\n
        A value of n includes today and the previous n-1 days.\n`,
    },
    { value: "nextDays", label: "Next Days…", icon: "calendar-day" },
  ],
  boolean: [{ value: "==", label: "Equal", icon: "equals" }],
};

export const dateRangeOperators = ["range"];
export const LAST_X_DAYS_OPERATOR = "lastDays";
export const NEXT_X_DAYS_OPERATOR = "nextDays";
export const LAST_X_DAYS_NO_TODAY_OPERATOR = "lastDaysNoToday";

export default function FieldOperatorList(props) {
  const [showList, setShowList] = useState(false);
  const [dimensions, setDimensions] = useState(null);
  const ref = useRef(null);
  const listRef = useRef(null);
  useOnClickOutside(ref, () => setShowList(false));

  const handleSelect = (val) => {
    setShowList(false);
    props.setValue(val.value);
  };

  const typeBucket = buckets[props.type] || "string";

  const icon = () =>
    props.value
      ? operators[typeBucket].find((op) => op.value === props.value)?.icon ||
        "bug"
      : "question";

  const handleToggle = () => {
    setDimensions(listRef.current.getBoundingClientRect());
    setShowList(!showList);
  };

  return (
    <Container>
      <Value data-cy="operator-select" onClick={handleToggle} ref={listRef}>
        <FontAwesomeIcon icon={["fas", icon()]} />
      </Value>

      {showList ? (
        <ExplorerSelectPortal>
          <Menu dimensions={dimensions} ref={ref}>
            {operators[typeBucket].map((op) => (
              <Option op={op} handleSelect={handleSelect} key={op.value} />
            ))}
          </Menu>
        </ExplorerSelectPortal>
      ) : null}
    </Container>
  );
}

FieldOperatorList.propTypes = {
  type: PropTypes.string,
  value: PropTypes.string,
  setValue: PropTypes.func,
  position: PropTypes.number,
};
